import React, { useState } from 'react';
import { X } from 'lucide-react';

const WarehouseItemModal = ({ isOpen, onClose, item, onSave }) => {
  const [itemData, setItemData] = useState({
    name: '',
    description: '',
    amountType: 'pcs',
    assignedWarehouses: [],
    ...item
  });

  const [errors, setErrors] = useState({});

  const amountTypes = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'meters', label: 'Meters' },
    { value: 'kilograms', label: 'Kilograms' },
    { value: 'liters', label: 'Liters' }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!itemData.name) newErrors.name = 'Name is required';
    if (!itemData.amountType) newErrors.amountType = 'Amount type is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(itemData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {item ? 'Edit Item' : 'New Item'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white rounded-lg border p-4 shadow-sm">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  value={itemData.name}
                  onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  rows="3"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={itemData.description}
                  onChange={(e) => setItemData({ ...itemData, description: e.target.value })}
                  placeholder="Enter item description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Amount Type *</label>
                <select
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={itemData.amountType}
                  onChange={(e) => setItemData({ ...itemData, amountType: e.target.value })}
                >
                  {amountTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Price Information Card */}
          <div className="bg-white rounded-lg border p-4 shadow-sm">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Price Information</h4>
            <div className="bg-gray-50 rounded p-4">
              {item?.price > 0 ? (
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Average Price:</span>
                    <span className="text-lg font-semibold text-gray-900">
                      ${item.price.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Based on income records
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Price will be calculated automatically based on income records
                </p>
              )}
            </div>
          </div>

          {/* Warehouse Information Card */}
          <div className="bg-white rounded-lg border p-4 shadow-sm">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Warehouse Information</h4>
            <div className="bg-gray-50 rounded p-4">
              {item && item.assignedWarehouses.filter(assignment => assignment.amount > 0).length > 0 ? (
                <div className="space-y-2">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-500">Current Stock:</span>
                  </div>
                  {item.assignedWarehouses
                    .filter(assignment => assignment.amount > 0)
                    .map((assignment) => (
                    <div 
                      key={assignment.warehouse._id} 
                      className="flex justify-between items-center p-2 bg-white rounded border border-gray-100"
                    >
                      <span className="text-sm text-gray-600">{assignment.warehouse.name}</span>
                      <span className="text-sm font-medium">
                        {assignment.amount} {itemData.amountType}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Stock levels will be updated automatically based on income and outcome records
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              {item ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarehouseItemModal;